import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDemoTenant } from '@/hooks/useDemoTenant';
import { DEMO_TOUR_STEPS, type TourPlacement, type TourStep } from './tourSteps';

const SESSION_KEY = 'vp_demo_tour_seen';

function queryTarget(target?: string): HTMLElement | null {
  if (!target) return null;
  return document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
}

export type GuidedTourState = {
  isDemo: boolean;
  active: boolean;
  step: TourStep | undefined;
  index: number;
  total: number;
  isLast: boolean;
  rect: DOMRect | null;
  placement: TourPlacement;
  next: () => void;
  prev: () => void;
  close: () => void;
};

/** Contrôleur du tour guidé : éligibilité, étape courante, navigation, surbrillance. */
export function useGuidedTour(): GuidedTourState {
  const isDemo = useDemoTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const step = DEMO_TOUR_STEPS[index];
  const total = DEMO_TOUR_STEPS.length;
  const isLast = index === total - 1;

  // Auto-lancement une fois par session de navigation
  useEffect(() => {
    if (!isDemo) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, '1');
    setIndex(0);
    setActive(true);
  }, [isDemo]);

  // Navigation vers la route de l'étape
  useEffect(() => {
    if (!active || !step) return;
    if (location.pathname !== step.route) navigate(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  // Localisation de la zone à mettre en avant (avec attente du rendu)
  useEffect(() => {
    if (!active || !step) return;
    let timer = 0;
    let tries = 0;
    const locate = () => {
      const el = queryTarget(step.target);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setRect(el.getBoundingClientRect());
      } else if (step.target && tries < 20) {
        tries += 1;
        timer = window.setTimeout(locate, 120);
      } else {
        setRect(null);
      }
    };
    setRect(null);
    timer = window.setTimeout(locate, 350);
    return () => window.clearTimeout(timer);
  }, [active, index, step, location.pathname]);

  // Recalcule la zone en cas de resize/scroll
  useEffect(() => {
    if (!active || !step?.target) return;
    const update = () => {
      const el = queryTarget(step.target);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, index, step]);

  const close = useCallback(() => {
    setActive(false);
    setRect(null);
  }, []);
  const next = useCallback(() => {
    setIndex((i) => (i >= total - 1 ? i : i + 1));
    if (isLast) close();
  }, [isLast, total, close]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const placement: TourPlacement = rect ? 'center' : (step?.placement ?? 'center');

  return {
    isDemo,
    active,
    step,
    index,
    total,
    isLast,
    rect,
    placement,
    next,
    prev,
    close,
  };
}
